import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAlt";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";

import moment from "moment";

import Langs, { Localize } from "../../../app/lang/langs";
import NetworkManager from "../../../app/managers/networkManager";
import { getVLearnActivity } from "../../../containers/vlearn/vlearnSlice";
import { VLearnEmptyState, vlearnSurface, vlearnType } from "../vlearnUi";

/**
 * One icon and one colour per action, in a single lookup.
 *
 * The legend below and the rows above both read from this object, which is the point: two
 * lists — one for the legend, one for the rows — is how a feed comes to show a green tick
 * next to a label that says "downloaded a file". The keys are the `vlearn_activity_log.action`
 * enum exactly as `ACTIVITY_ACTIONS` in VLearnProgressController spells them; the filter is
 * validated against that enum server-side, so a key invented here would silently return
 * nothing.
 */
const ACTION_META = {
    course_open: { Icon: LoginRoundedIcon, color: '#4b5263', key: 'VLEARN_ACTION_COURSE_OPEN' },
    lesson_open: { Icon: PlayCircleOutlineIcon, color: '#1179be', key: 'VLEARN_ACTION_LESSON_OPEN' },
    lesson_complete: { Icon: TaskAltRoundedIcon, color: 'var(--green, rgb(58,156,96))', key: 'VLEARN_ACTION_LESSON_COMPLETE' },
    file_download: { Icon: FileDownloadRoundedIcon, color: '#0067b8', key: 'VLEARN_ACTION_FILE_DOWNLOAD' },
    quiz_start: { Icon: QuizOutlinedIcon, color: '#8E4EC6', key: 'VLEARN_ACTION_QUIZ_START' },
    quiz_submit: { Icon: FactCheckOutlinedIcon, color: '#6D28D9', key: 'VLEARN_ACTION_QUIZ_SUBMIT' },
    announcement_read: { Icon: CampaignOutlinedIcon, color: 'var(--orange, rgb(255,144,21))', key: 'VLEARN_ACTION_ANNOUNCEMENT_READ' },
    course_complete: { Icon: EmojiEventsOutlinedIcon, color: '#1F7A4C', key: 'VLEARN_ACTION_COURSE_COMPLETE' },
    certificate_issued: { Icon: WorkspacePremiumOutlinedIcon, color: '#B25E00', key: 'VLEARN_ACTION_CERTIFICATE_ISSUED' }
}

const ACTION_KEYS = Object.keys(ACTION_META)

/**
 * How many events one request asks for.
 *
 * `getActivityFeed` clamps `limit` to 200 — it is a feed, not an export, and an endpoint that
 * can return a course's entire history in one response is an export endpoint in disguise. 50
 * is one comfortable screenful; the ceiling is stated here so a future edit that raises this
 * number cannot quietly exceed what the server will honour.
 */
const PAGE_SIZE = 50
const SERVER_MAX_LIMIT = 200

/**
 * The raw event stream, newest first — who did what, to which lesson, and when.
 *
 * This is the question the progress tables cannot answer. `vlearn_lesson_progress` knows a
 * lesson was opened seven times; only this knows that six of those were in the ten minutes
 * before the deadline. Rows come from `VLearnProgressController.getActivityFeed`, which sends
 * `{ id, id_lesson, id_collab, action, meta, created_at, nom, prenom, email,
 * photo_updated_at, lesson_title, lesson_type }`.
 *
 * ── Why "load more" keeps its own copy of the earlier pages ───────────────────────────
 *
 * The slice sets `dashboardActivity` to the response's list rather than appending to it, and
 * adding a reducer that appends is out of scope. So the component holds the pages it has
 * already shown and concatenates them ahead of the page currently in the store. The offset
 * grows by `PAGE_SIZE` per click, which is what the server paginates on; changing any filter
 * discards the accumulation, because page three of the unfiltered feed is not page three of
 * the filtered one.
 *
 * @param {object} props
 * @param {number} props.idCourse
 */
export default function ActivityFeed({ idCourse }) {

    const dispatch = useDispatch()

    const page = useSelector((state) => state.vlearn.dashboardActivity)
    const learners = useSelector((state) => state.vlearn.dashboardLearners)
    const lessons = useSelector((state) => state.vlearn.dashboardLessons)
    const retentionDays = useSelector((state) => state.vlearn.settings.activity_log_retention_days)
    const lang = useSelector((state) => state.settings.lang)

    const [idCollab, setIdCollab] = useState("")
    const [idLesson, setIdLesson] = useState("")
    const [action, setAction] = useState("")
    const [offset, setOffset] = useState(0)
    /** The pages already scrolled past, kept because the slice replaces rather than appends. */
    const [earlierPages, setEarlierPages] = useState([])

    // React 18's StrictMode mounts twice in development; without this the feed opens with two
    // identical requests. It also absorbs re-renders that change nothing this endpoint reads.
    const lastQuery = useRef(null)

    useEffect(() => {
        if (!idCourse) return
        const body = {
            id_course: idCourse,
            id_collab: idCollab || undefined,
            id_lesson: idLesson || undefined,
            action: action || undefined,
            limit: Math.min(PAGE_SIZE, SERVER_MAX_LIMIT),
            offset
        }
        const key = JSON.stringify(body)
        if (lastQuery.current === key) return
        lastQuery.current = key
        dispatch(getVLearnActivity(body))
    }, [dispatch, idCourse, idCollab, idLesson, action, offset])

    /** Any filter change restarts the feed from the top. */
    const onFilterChange = useCallback((setter) => (event) => {
        setter(event.target.value)
        setEarlierPages([])
        setOffset(0)
    }, [])

    const rows = useMemo(() => {
        // Deduplicated on the row id: a lesson completed between two "load more" clicks
        // shifts every later row down by one, so the same event can legitimately arrive in
        // two consecutive pages.
        const seen = new Set()
        const merged = []
        for (const row of [...earlierPages, ...(page || [])]) {
            const id = String(row.id)
            if (seen.has(id)) continue
            seen.add(id)
            merged.push(row)
        }
        return merged
    }, [earlierPages, page])

    // A page that came back full is a page that probably has a successor. There is no total
    // in the slice for this endpoint, so a short page is the end-of-feed signal.
    const hasMore = (page || []).length >= PAGE_SIZE

    const onLoadMore = useCallback(() => {
        setEarlierPages(rows)
        setOffset((current) => current + PAGE_SIZE)
    }, [rows])

    /**
     * The collaborator filter's options.
     *
     * The enrolled roster the Learners tab is holding, unioned with anyone appearing in the
     * feed — a learner whose enrolment was since deactivated still has events in the log, and
     * dropping them from the menu would make their history unreachable.
     */
    const collabOptions = useMemo(() => {
        const byId = new Map()
        for (const row of (learners || [])) {
            byId.set(String(row.id_collab), {
                id: row.id_collab,
                name: [row.prenom, row.nom].filter(Boolean).join(' ')
            })
        }
        for (const row of rows) {
            const id = String(row.id_collab)
            if (byId.has(id)) continue
            byId.set(id, {
                id: row.id_collab,
                name: [row.prenom, row.nom].filter(Boolean).join(' ') || row.email
            })
        }
        return Array.from(byId.values())
            .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    }, [learners, rows])

    /** The lesson filter's options — the full curriculum, from the Lessons tab's own fetch. */
    const lessonOptions = useMemo(
        () => (lessons || []).map((lesson) => ({ id: lesson.id, title: lesson.title })),
        [lessons]
    )

    /**
     * "2 hours ago", with the exact timestamp one hover away.
     *
     * Relative is what a reader actually wants from a feed, and absolute is what they need the
     * moment they are reconciling it against something else — so both, rather than a choice.
     * `.locale(lang)` on the instance: the global is set once from the stored language in
     * app/lang/langs.js, and a component that reassigns it changes every other screen too.
     */
    const relative = useCallback((value) => {
        const parsed = moment(value)
        if (!parsed.isValid()) return { short: '—', exact: '' }
        const localised = parsed.locale(lang || 'fr')
        return { short: localised.fromNow(), exact: localised.format('DD/MM/YYYY HH:mm') }
    }, [lang])

    const selectSx = { flex: '0 1 200px', fontSize: 13 }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>

            {/* ── Filters ─────────────────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <Select
                    size="small"
                    value={idCollab}
                    displayEmpty
                    inputProps={{ 'aria-label': Localize("VLEARN_FILTER_COLLAB_ALL", lang) }}
                    onChange={onFilterChange(setIdCollab)}
                    sx={selectSx}>
                    <MenuItem value="" sx={{ fontSize: 13 }}>
                        <Langs str="VLEARN_FILTER_COLLAB_ALL" />
                    </MenuItem>
                    {collabOptions.map((collab) => (
                        <MenuItem key={collab.id} value={collab.id} sx={{ fontSize: 13 }}>
                            {collab.name}
                        </MenuItem>
                    ))}
                </Select>

                <Select
                    size="small"
                    value={idLesson}
                    displayEmpty
                    inputProps={{ 'aria-label': Localize("VLEARN_FILTER_LESSON_ALL", lang) }}
                    onChange={onFilterChange(setIdLesson)}
                    sx={selectSx}>
                    <MenuItem value="" sx={{ fontSize: 13 }}>
                        <Langs str="VLEARN_FILTER_LESSON_ALL" />
                    </MenuItem>
                    {lessonOptions.map((lesson) => (
                        <MenuItem key={lesson.id} value={lesson.id} sx={{ fontSize: 13 }}>
                            {lesson.title}
                        </MenuItem>
                    ))}
                </Select>

                <Select
                    size="small"
                    value={action}
                    displayEmpty
                    inputProps={{ 'aria-label': Localize("VLEARN_FILTER_ACTION_ALL", lang) }}
                    onChange={onFilterChange(setAction)}
                    sx={selectSx}>
                    <MenuItem value="" sx={{ fontSize: 13 }}>
                        <Langs str="VLEARN_FILTER_ACTION_ALL" />
                    </MenuItem>
                    {ACTION_KEYS.map((value) => (
                        <MenuItem key={value} value={value} sx={{ fontSize: 13 }}>
                            {Localize(ACTION_META[value].key, lang)}
                        </MenuItem>
                    ))}
                </Select>

                <Box sx={{ flex: 1 }} />

                <Typography sx={{ ...vlearnType.meta, fontVariantNumeric: 'tabular-nums' }}>
                    <Langs
                        str={rows.length === 1
                            ? "VLEARN_ACTIVITY_EVENTS_ONE" : "VLEARN_ACTIVITY_EVENTS_MANY"}
                        params={{ count: rows.length }}
                    />
                </Typography>
            </Box>

            {/*
              * The retention notice sits in the UI, not only in a policy document, because a
              * teacher hunting for last September's activity would otherwise read an empty
              * feed as a bug in the platform. Telling them the history has a horizon — and
              * why it has one — is the difference between a support ticket and an answer.
              */}
            <Box sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1,
                backgroundColor: 'rgba(0, 40, 128, 0.04)',
                border: '1px solid rgba(0, 40, 128, 0.08)',
                borderRadius: '10px', px: 1.5, py: 1.25
            }}>
                <InfoOutlinedIcon sx={{ fontSize: 17, color: 'var(--vital-blue, #002880)', mt: '1px' }}
                    aria-hidden="true" />
                <Typography sx={{ ...vlearnType.meta, lineHeight: 1.5 }}>
                    <Langs str="VLEARN_ACTIVITY_RETENTION_NOTICE"
                        params={{ days: retentionDays }} />
                </Typography>
            </Box>

            {/* ── The timeline ────────────────────────────────────────────────────── */}
            {rows.length === 0 ? (
                <Box sx={{ ...vlearnSurface.card }}>
                    <VLearnEmptyState
                        icon={<HistoryRoundedIcon />}
                        title={<Langs str="VLEARN_NO_ACTIVITY_TITLE" />}
                        description={<Langs str="VLEARN_NO_ACTIVITY_BODY" />}
                    />
                </Box>
            ) : (
                <Box sx={{ ...vlearnSurface.card, px: { xs: 1.5, sm: 2.5 }, py: 2 }}>
                    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                        {rows.map((row, index) => {
                            const meta = ACTION_META[row.action]
                                || { Icon: HistoryRoundedIcon, color: '#4b5263', key: null }
                            const Icon = meta.Icon
                            const when = relative(row.created_at)
                            const photoUrl = NetworkManager.getInstance()
                                .getCollabPhotoUrl(row.id_collab, row.photo_updated_at)
                            const name = [row.prenom, row.nom].filter(Boolean).join(' ') || row.email

                            return (
                                <Box
                                    component="li"
                                    key={row.id}
                                    sx={{ display: 'flex', gap: 1.5, position: 'relative', pb: 2 }}
                                >
                                    {/* The connecting rail, drawn on every row but the last so
                                        the column reads as one thread rather than nine cards. */}
                                    {index < rows.length - 1 ? (
                                        <Box
                                            aria-hidden="true"
                                            sx={{
                                                position: 'absolute', left: 15, top: 32, bottom: 0,
                                                width: '1px', backgroundColor: 'rgba(0,0,0,0.08)'
                                            }}
                                        />
                                    ) : null}

                                    <Box sx={{
                                        width: 31, height: 31, borderRadius: '50%', flexShrink: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: '#fff',
                                        border: `1px solid ${meta.color}`,
                                        zIndex: 1
                                    }}>
                                        <Icon sx={{ fontSize: 16, color: meta.color }} aria-hidden="true" />
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap'
                                        }}>
                                            <Avatar
                                                src={photoUrl || undefined}
                                                sx={{
                                                    width: 20, height: 20, fontSize: 9,
                                                    bgcolor: 'var(--lightGrey, #ededed)', color: '#6f6f6f'
                                                }}>
                                                {[row.prenom, row.nom].filter(Boolean)
                                                    .map((part) => String(part).charAt(0).toUpperCase()).join('')}
                                            </Avatar>
                                            <Typography component="span" sx={{ ...vlearnType.cardTitle }}>
                                                {name}
                                            </Typography>
                                            <Typography component="span" sx={{ ...vlearnType.body }}>
                                                {meta.key ? Localize(meta.key, lang) : row.action}
                                            </Typography>
                                        </Box>

                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.25
                                        }}>
                                            {row.lesson_title ? (
                                                <Typography sx={{
                                                    ...vlearnType.meta, fontWeight: 600,
                                                    color: 'var(--text-secondary, #4b5263)'
                                                }}>
                                                    {row.lesson_title}
                                                </Typography>
                                            ) : null}
                                            <Tooltip title={when.exact} arrow>
                                                <Typography
                                                    component="time"
                                                    dateTime={row.created_at || undefined}
                                                    title={when.exact}
                                                    sx={{ ...vlearnType.meta, cursor: 'help' }}
                                                >
                                                    {when.short}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Box>
                            )
                        })}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                        {hasMore ? (
                            <Button size="small" variant="outlined" onClick={onLoadMore}
                                sx={{ textTransform: 'none' }}>
                                <Langs str="VLEARN_LOAD_MORE" />
                            </Button>
                        ) : (
                            <Typography sx={{ ...vlearnType.meta }}>
                                <Langs str="VLEARN_ACTIVITY_ALL_LOADED" />
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            {/* ── Legend ──────────────────────────────────────────────────────────── */}
            {/* Reads from ACTION_META, the same object the rows above use, so the legend and
                the timeline cannot come to disagree about what an icon means. */}
            <Box>
                <Typography sx={{
                    ...vlearnType.meta, textTransform: 'uppercase',
                    letterSpacing: '.04em', fontWeight: 700, mb: 0.75
                }}>
                    <Langs str="VLEARN_ACTIVITY_LEGEND" />
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.75, flexWrap: 'wrap' }}>
                    {ACTION_KEYS.map((value) => {
                        const meta = ACTION_META[value]
                        const Icon = meta.Icon
                        return (
                            <Box key={value} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Icon sx={{ fontSize: 15, color: meta.color }} aria-hidden="true" />
                                <Typography sx={{ ...vlearnType.meta }}>
                                    {Localize(meta.key, lang)}
                                </Typography>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </Box>
    )
}
